import { Stack, Typography } from '@mui/material';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type { EstimateBreakdown } from '@/features/operations/types';

/**
 * The arithmetic behind an automatic amount, from the copy stored on the quotation itself.
 *
 * Quotations emitted before the rule versions were dismantled carry only the label of the
 * version that produced them: those were calculated by multiplying percentages, so there is no
 * per-damage amount to show that anybody ever decided. Saying so is more honest than
 * reconstructing one.
 */
export function EstimateBreakdownDetail({
  breakdown,
  currency,
}: {
  breakdown: EstimateBreakdown | null;
  currency: string;
}) {
  const { formatMoney } = usePriceDisplay();
  if (!breakdown) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sin desglose registrado.
      </Typography>
    );
  }

  if (breakdown.source !== 'CONFIGURED_PRICES') {
    return (
      <Typography variant="caption" color="text.secondary">
        {breakdown.ruleVersionLabel
          ? `Calculada con la regla ${breakdown.ruleVersionLabel}, anterior a los montos por modelo.`
          : 'Calculada con una configuración anterior a los montos por modelo.'}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25} mt={0.5}>
      <Typography variant="caption" color="text.secondary">
        Precio base {formatMoney(breakdown.baseAmountMinor ?? 0, breakdown.currency || currency)}
      </Typography>
      {(breakdown.lines || []).map((line) => (
        <Typography key={line.fieldLabel} variant="caption" color="text.secondary">
          − {formatMoney(line.discountAmountMinor, breakdown.currency || currency)} ·{' '}
          {line.fieldLabel}: {line.answerLabel}
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        Los montos son los que estaban cargados cuando se emitió la cotización.
      </Typography>
    </Stack>
  );
}
